// TableSettings.js
import React, { useState } from "react";
import TableSettingModal from "./TableSettingModal";
import config from "../configs/config";
import "./styles/PropertiesComponent.css";
import CommonButton from "../elments/CommonButton";
import Loader from "./Loader";

const PropertiesComponent = ({
  selectedVariable,
  setdataGroup,
  dataGroup,
  selectedKey,
  setSelectedVariable,
  projectId,
}) => {
  const [activeTab, setActiveTab] = useState("properties");
  const [nets, setNets] = useState([]);
  const [netLabel, setNetLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiValidationLoading, setAiValidationLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  // Check if selectedVariable exists
  if (!selectedVariable) {
    return (
      <div className="variable-properties">
        <h2>No variable selected</h2>
        <p>Please select a variable from the list to view its properties.</p>
      </div>
    );
  }

  const handleConvertTo3D = () => {
    if (!selectedVariable || !selectedVariable.type.includes("2DGrid")) return;

    const rowMap = new Map();
    const colMap = new Map();

    const canConvert = selectedVariable.Rows.every((row) => {
      const match = row.index.match(/^(\d+)c(\d+)$/);
      return !!match;
    });

    if (!canConvert) {
      alert("Cannot convert: Irregular row index pattern.");
      return;
    }

    selectedVariable.Rows.forEach((row) => {
      const match = row.index.match(/^(\d+)c(\d+)$/);
      if (match) {
        const r = match[1];
        const c = match[2];
        const parts = row.title.split(" - ");
        if (parts.length === 2) {
          const statement = parts[0];
          const brand = parts[1];
          if (!rowMap.has(r)) {
            const newRow = { index: r, title: statement };
            if (row.exclusive) newRow.exclusive = true;
            rowMap.set(r, newRow);
          }
          if (!colMap.has(c)) {
            colMap.set(c, { index: c, title: brand });
          }
        }
      }
    });

    // Sort rows, with 99 last
    const newRows = Array.from(rowMap.values()).sort((a, b) => {
      const ai = a.index === "99" ? 100 : parseInt(a.index);
      const bi = b.index === "99" ? 100 : parseInt(b.index);
      return ai - bi;
    });

    // Sort cols
    const newCols = Array.from(colMap.values()).sort(
      (a, b) => parseInt(a.index) - parseInt(b.index)
    );

    // Update varName
    const newVarName = selectedVariable.varName.map((vn) => {
      const match = vn.row.match(/^(\d+)c(\d+)$/);
      if (match) {
        return { ...vn, row: match[1], col: match[2] };
      }
      return vn;
    });

    // Update selectedVariable
    setSelectedVariable((prev) => ({
      ...prev,
      type: prev.type.replace("2D", "3D"),
      Rows: newRows,
      Cols: newCols,
      varName: newVarName,
      grid: true,
      reverseGrid: true,
      valueLabels: { "0.0": "No", "1.0": "Yes" },
      values: [
        { index: 0, title: "No" },
        { index: 1, title: "Yes" },
      ],
    }));
  };

  const createNet = (label, rowIndices) => {
    const newNet = {
      label: label,
      indices: rowIndices,
    };
    setNets((prevNets) => {
      const updatedNets = [...prevNets, newNet];
      addNet(updatedNets);
      return updatedNets;
    });
  };

  const defaultNewProperties = {
    removeTab: false,
    mean: false,
    median: false,
    grid: false,
    reverseGrid: false,
    addNets: false,
    saveToExcel: false,
    downloadDVExcel: false,
    SPSSSyntax: false,
    conceptTables: false,
    reverseLocation: false,
    dataCuts: false,
    SE: false,
    STD: false,
    rank: false,
    rankalphabetical: false,
    rankCol: false,
  };

  const addNet = (updatedNets) => {
    setSelectedVariable((prev) => ({ ...prev, Nets: updatedNets }));
  };

  const formData = new FormData();

  const saveChanges = async () => {
    if (!selectedVariable) return;

    setLoading(true);
    const updatedDataGroup = { ...dataGroup, [selectedKey]: selectedVariable };
    formData.append("projectId", projectId);
    formData.append("dataGroup", JSON.stringify(updatedDataGroup));

    setdataGroup((prev) => ({ ...prev, [selectedKey]: selectedVariable }));
    setNets([]);
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/save_all_data_list`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        alert("Failed to save data to the backend");
        throw new Error("Failed to save dataGroup to the backend");
      }

      alert("Data saved successfully");
    } catch (error) {
      alert("Error saving changes:" + error.message);
    } finally {
      setLoading(false);
    }
  };

  const isBoolean = (property) =>
    typeof defaultNewProperties[property] === "boolean";

  const handleRowsChange = (updatedRows) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Rows: updatedRows }));
    }
  };

  const handleColsChange = (updatedCols) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, Cols: updatedCols }));
    }
  };

  const handlevaluesChange = (updatedValues) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({ ...prev, values: updatedValues }));
    }
  };

  const handlevalueLabelsChange = (updatedvalueLabels) => {
    if (selectedVariable) {
      setSelectedVariable((prev) => ({
        ...prev,
        valueLabels: updatedvalueLabels,
      }));
    }
  };

  const handlePropertyChange = (e, property) => {
    if (!selectedVariable) return;

    const newValue = isBoolean(property) ? e.target.checked : e.target.value;
    setSelectedVariable((prev) => ({ ...prev, [property]: newValue }));
  };

  // Function to render content based on active tab
  const renderTabContent = () => {
    if (!selectedVariable) return <div>No variable data available</div>;

    switch (activeTab) {
      case "properties":
        return (
          <div className="properties-container">
            <div className="checkbox-properties">
              <div key="allstats" className="property-item">
                <label>All Stats</label>
                <input
                  type="checkbox"
                  checked={selectedVariable.allstats || false}
                  onChange={(e) => handlePropertyChange(e, "allstats")}
                />
              </div>
              <label>Combo Table</label>
              <input
                type="text"
                value={selectedVariable.combo || ""}
                placeholder="S3 or S4, S3 and S4;S3 and S4"
                onChange={(e) => handlePropertyChange(e, "combo")}
              />
              <label>Filtered Tables</label>
              <input
                type="text"
                value={selectedVariable.flttb || ""}
                placeholder="S3, S4=1-4 etc"
                onChange={(e) => handlePropertyChange(e, "flttb")}
              />
              {(selectedVariable.type == "Single2DGrid" ||
                selectedVariable.type == "Multi3DGrid") && (
                <>
                  <label>Stacked Table</label>
                  <input
                    type="text"
                    value={selectedVariable.stacked || ""}
                    placeholder="1,3,4;1,6,9"
                    onChange={(e) => handlePropertyChange(e, "stacked")}
                  />
                </>
              )}
              {(selectedVariable.type == "Numeric" ||
                selectedVariable.type == "Numeric2DGrid" ||
                selectedVariable.type == "Numeric3DGrid") && (
                <>
                  <label>NETS</label>
                  <input
                    type="text"
                    value={selectedVariable.nets || ""}
                    placeholder="NET1=1-5;NET2=6-10;11-15"
                    onChange={(e) => handlePropertyChange(e, "nets")}
                  />
                </>
              )}

              {Object.entries(selectedVariable).map(
                ([property, value]) =>
                  isBoolean(property) &&
                  !value.type && (
                    <div key={property} className="property-item">
                      <label>{property}</label>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => handlePropertyChange(e, property)}
                      />
                    </div>
                  )
              )}
              {Object.entries(selectedVariable).map(
                ([property, value]) =>
                  isBoolean(property) &&
                  value.type &&
                  value.type.includes(selectedVariable.type) && (
                    <div key={property} className="property-item">
                      <label>{property}</label>
                      <input
                        type="checkbox"
                        checked={value.selected}
                        onChange={(e) => handlePropertyChange(e, property)}
                      />
                    </div>
                  )
              )}
            </div>

            <div className="text-properties">
              {Object.entries(selectedVariable).map(
                ([property, value]) =>
                  !isBoolean(property) &&
                  property !== "error" &&
                  property !== "order" &&
                  property !== "allstats" &&
                  property !== "table" &&
                  property !== "nets" &&
                  property !== "flttb" &&
                  property != "combo" &&
                  property !== "values" &&
                  property !== "varName" &&
                  property !== "Cols" &&
                  property !== "Rows" &&
                  property !== "valueLabels" && (
                    <div key={property} className="property-item">
                      <label>{property}</label>
                      <input
                        type="text"
                        value={
                          Array.isArray(value)
                            ? value.map((obj) => obj.title).join(", ")
                            : value
                        }
                        onChange={(e) => handlePropertyChange(e, property)}
                      />
                    </div>
                  )
              )}
            </div>
          </div>
        );

      case "rows":
        return selectedVariable.Rows && selectedVariable.Rows.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>Rows Management ({selectedVariable.Rows.length} rows)</h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.Rows}
                onClose={() => {}} // Empty function since we're not closing modal
                onRowsChange={handleRowsChange}
                typeofproperty={"rows"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
          </div>
        ) : (
          <div className="no-data-message">No rows data available</div>
        );

      case "cols":
        return selectedVariable.Cols && selectedVariable.Cols.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>
                Columns Management ({selectedVariable.Cols.length} columns)
              </h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.Cols}
                onClose={() => {}} // Empty function since we're not closing modal
                onRowsChange={handleColsChange}
                typeofproperty={"cols"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
          </div>
        ) : (
          <div className="no-data-message">No columns data available</div>
        );

      case "values":
        return selectedVariable.values && selectedVariable.values.length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>
                Values Management ({selectedVariable.values.length} values)
              </h3>
            </div>
            <div className="modal-body">
              <TableSettingModal
                isOpen={true}
                rows={selectedVariable.values}
                onClose={() => {}} // Empty function since we're not closing modal
                onRowsChange={handlevaluesChange}
                typeofproperty={"values"}
                createNet={createNet}
                netLabel={netLabel}
                setNetLabel={setNetLabel}
              />
            </div>
          </div>
        ) : (
          <div className="no-data-message">No values data available</div>
        );

      case "valueLabels":
        return selectedVariable.valueLabels &&
          Object.keys(selectedVariable.valueLabels).length > 0 ? (
          <div className="modal-content-container">
            <div className="modal-header">
              <h3>
                Value Labels Management (
                {Object.keys(selectedVariable.valueLabels).length} labels)
              </h3>
            </div>
            <div className="modal-body">
              {/* Convert valueLabels object to array format */}
              {(() => {
                const valueLabelsArray = Object.entries(
                  selectedVariable.valueLabels
                ).map(([key, value]) => ({
                  index: key,
                  title: value,
                }));

                return (
                  <TableSettingModal
                    isOpen={true}
                    rows={valueLabelsArray}
                    onClose={() => {}} // Empty function since we're not closing modal
                    onRowsChange={(updatedRows) => {
                      // Convert array back to object
                      const updatedValueLabels = {};
                      updatedRows.forEach((row) => {
                        updatedValueLabels[row.index] = row.title;
                      });
                      handlevalueLabelsChange(updatedValueLabels);
                    }}
                    typeofproperty={"valueLabels"}
                    createNet={createNet}
                    netLabel={netLabel}
                    setNetLabel={setNetLabel}
                  />
                );
              })()}
            </div>
          </div>
        ) : (
          <div className="no-data-message">No value labels data available</div>
        );

      default:
        return <div>Select a tab to view content</div>;
    }
  };

  // AI Validation Function
  const handleAIValidation = async () => {
    if (!selectedVariable) {
      alert("No variable properties to validate");
      return;
    }

    setAiValidationLoading(true);
    setAiSuggestions(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/validate-with-ai`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          variableData: selectedVariable,
          variableName: selectedKey,
        }),
      });

      if (!response.ok) throw new Error("Failed to validate with AI");

      const result = await response.json();

      if (result.success) {
        setAiSuggestions(result.suggestions);
      } else {
        alert("AI validation failed: " + result.message);
      }
    } catch (error) {
      console.error("Error during AI validation:", error);
      alert("Error during AI validation: " + error.message);
    } finally {
      setAiValidationLoading(false);
    }
  };

  const applySuggestion = (suggestionType, suggestion) => {
    // console.log ('Applying suggestion:', suggestionType, suggestion);

    if (suggestionType === "text_correction" && suggestion.recommendation) {
      alert(`Applied text correction: ${suggestion.recommendation}`);
    }

    if (suggestionType === "sorting" && suggestion.recommendation) {
      alert(`Applied sorting suggestion: ${suggestion.recommendation}`);
    }
  };

  // Helper functions to check if data exists
  const hasRows =
    selectedVariable &&
    selectedVariable.Rows &&
    selectedVariable.Rows.length > 0;
  const hasCols =
    selectedVariable &&
    selectedVariable.Cols &&
    selectedVariable.Cols.length > 0;
  const hasValues =
    selectedVariable &&
    selectedVariable.values &&
    selectedVariable.values.length > 0;
  const hasValueLabels =
    selectedVariable &&
    selectedVariable.valueLabels &&
    Object.keys(selectedVariable.valueLabels).length > 0 &&
    1 == 2;

  return (
    <div className="variable-properties">
      <h2>{selectedKey || "No variable selected"}</h2>

      {/* Tab Navigation using CommonButton */}
      <div className="tab-navigation">
        <CommonButton
          title="Properties Tab"
          label="Properties"
          onClick={() => setActiveTab("properties")}
          variant="tab"
          isSelected={activeTab === "properties"}
          size="small"
          bold={true}
        />

        {hasRows && (
          <CommonButton
            title={`Rows Tab (${selectedVariable.Rows.length})`}
            label={`Rows (${selectedVariable.Rows.length})`}
            onClick={() => setActiveTab("rows")}
            variant="tab"
            isSelected={activeTab === "rows"}
            size="small"
            bold={true}
          />
        )}

        {hasCols && (
          <CommonButton
            title={`Columns Tab (${selectedVariable.Cols.length})`}
            label={`Columns (${selectedVariable.Cols.length})`}
            onClick={() => setActiveTab("cols")}
            variant="tab"
            isSelected={activeTab === "cols"}
            size="small"
            bold={true}
          />
        )}

        {hasValues && (
          <CommonButton
            title={`Values Tab (${selectedVariable.values.length})`}
            label={`Values (${selectedVariable.values.length})`}
            onClick={() => setActiveTab("values")}
            variant="tab"
            isSelected={activeTab === "values"}
            size="small"
            bold={true}
          />
        )}

        {hasValueLabels && (
          <CommonButton
            title={`Value Labels Tab (${
              Object.keys(selectedVariable.valueLabels).length
            })`}
            label={`Value Labels (${
              Object.keys(selectedVariable.valueLabels).length
            })`}
            onClick={() => setActiveTab("valueLabels")}
            variant="tab"
            isSelected={activeTab === "valueLabels"}
            size="small"
            bold={true}
          />
        )}
      </div>

      {/* AI Validation Section */}
      <div className="ai-validation-section">
        <CommonButton
          title="Validate with AI"
          label={aiValidationLoading ? "Validating..." : "Validate with AI"}
          onClick={handleAIValidation}
          disabled={aiValidationLoading || !selectedVariable}
          variant="purple"
          size="small"
          loading={aiValidationLoading}
        />
      </div>

      {/* AI Suggestions Display */}
      {aiSuggestions && (
        <div className="ai-suggestions">
          <h3>AI Suggestions</h3>
          <div className="suggestion-list">
            {aiSuggestions.map((suggestion, index) => (
              <div key={index} className="suggestion-item">
                <p>
                  <strong>{suggestion.type}:</strong> {suggestion.message}
                </p>
                {suggestion.recommendation && (
                  <p>
                    <strong>Recommendation:</strong> {suggestion.recommendation}
                  </p>
                )}
                <CommonButton
                  title={`Apply ${suggestion.type} suggestion`}
                  label="Apply Suggestion"
                  onClick={() => applySuggestion(suggestion.type, suggestion)}
                  variant="smallAction"
                  size="small"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabulation Syntax Display Section */}
      {selectedVariable && selectedVariable.tb && (
        <div className="tabulation-syntax-display">
          <h3>Generated Syntax</h3>
          <pre>{selectedVariable.tb}</pre>
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content">{renderTabContent()}</div>

      {/* Save Button - Only show if we have a selected variable */}
      {selectedVariable && (
        <div className="action-buttons">
          <CommonButton
            title="Save Changes"
            label={loading ? "Saving..." : "Save Changes"}
            onClick={saveChanges}
            variant="primary"
            size="large"
            loading={loading}
            bold={true}
          />
          {selectedVariable.type &&
            selectedVariable.type.includes("2DGrid") && (
              <CommonButton
                title="Convert 2D to 3D Grid"
                label="Convert to 3D"
                onClick={handleConvertTo3D}
                variant="secondary"
                size="large"
                bold={true}
              />
            )}
          {loading && <Loader loading={loading} />}
        </div>
      )}
    </div>
  );
};

export default PropertiesComponent;
